<template>
  <div>
    <el-card class="chart-card">
      <h1>对象映射</h1>
      <p>
        <el-text tag="b">前排提示：</el-text>
        <el-text>Pignoo要求的数据库映射对象是JavaBean，更负责任得说：</el-text>
        <br />
        <el-text>1. 必须要有属性，属性可以是任何作用域修饰符，不可以使用“空壳getter、setter”来代替属性；</el-text>
        <br />
        <el-text>2. 属性必须有规范命名的public getter、setter，命名规则参考Lombok；</el-text>
        <br />
        <el-text>3. 严格getter的入参、返回值：getter是无参且返回值类型与属性一致，setter是单参数、void返回且参数类型与属性一致；</el-text>
        <br />
        <el-text>4. 可以改写getter、setter，落入数据库的值以写入属性的值为准；</el-text>
        <br />
        <el-text>5. Pignoo持久化数据的方式是存入数据库，所以不需要Serializable。</el-text>
        <br />
        <el-text>6. 参考Lombok，Pignoo至少需要@Getter与@Setter，推荐@Data，禁止@Accessors(chain=true)。</el-text>
      </p>
      <p>
        <el-text tag="b">前排提示：</el-text>
        <el-text>Pignoo是一个弱化数据库概念、弱化映射关系的ORM框架。在真正使用Pignoo时（配合Pignoo自带的自动数据库扩展）你甚至无需关心数据库结构。</el-text>
        <el-text>但是你依然需要了解Pignoo是如何映射数据库的，这可以提升你对Pignoo设计方式的理解。</el-text>
      </p>
      <p>
        <el-text tag="b">前排提示：</el-text>
        <el-text>目前，Pignoo要求每个表都要有一个唯一主键！也就是</el-text>
        <el-text tag="b">不支持无主键表，不支持联合主键表。</el-text>
        <el-text>这是为了能更好得模拟List操作数据的体验。</el-text>
      </p>
      <h1>默认映射规则</h1>
      <p>
        <el-text>Pignoo支持使用注解进行映射，</el-text>
        <el-text tag="b">但是更推荐使用全局配置，与默认映射方式，从而做到不使用注解也能映射。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code1 }}</code></pre>
      </div>
      <h1>配置全局映射规则</h1>
      <p>
        <el-text>使用PignooConfig配置规则</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code2 }}</code></pre>
      </div>
      <p>
        <el-text>如上设置，映射关系就会变成这样：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code3 }}</code></pre>
      </div>
      <h1>使用注解</h1>
      <p>
        <el-text>因为注解是仅本类生效的，</el-text>
        <el-text tag="b">所以注解的优先级更高。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code4 }}</code></pre>
      </div>
      <h1>@Link</h1>
      <p>
        <el-text>将本实体映射到其他实体（Class）上，而不要使用@Table。例如在B上@Link(A)：</el-text>
        <br />
        <el-text>1. 会优先解析A实体类。</el-text>
        <br />
        <el-text>2. 然后再解析B实体类。</el-text>
        <br />
        <el-text>3. B映射的表名与A保持一致。</el-text>
        <br />
        <el-text>4. B中只保留与A能匹配上的字段，其他字段一律忽略，即使标记了@Column也会被忽略。</el-text>
      </p>
      <p>
        <el-text>Pignoo对实体类的认知有两种方式：类是表、类是可持久化的Java数据类型。</el-text>
        <el-text>“类是表”很好理解，依然是操作数据库的眼光去看到Pignoo，为实体类映射关系上增加各种配置、别名加以控制。</el-text>
        <el-text>但Pignoo的哲学是弱化数据库的存在，所以“类是可持久化的Java数据类型”是Pignoo更推荐的！</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code5 }}</code></pre>
      </div>
      <h1>数据类型映射关系</h1>
      <el-text>Pignoo使用的是JDBC默认映射方式。以下是推荐的映射规则，但可以不局限于这个映射规则。</el-text>
      <el-text>例如：Java中使用Integer，SQL中使用int、bigint都可以，但要注意取值范围问题。</el-text>
      <div style="width: 500px;margin: auto;">
        <el-table :data="dataTypeTable" style="width: 100%;" size="small" border="true">
          <el-table-column prop="javaType" label="Java类型" />
          <el-table-column prop="sqlType" label="SQL类型" />
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

onMounted(() => {
  if (window.hljs) {
    window.hljs.highlightAll()
  }
})

const code1 = `@Data
public class PigTable { // 数据库表默认驼峰映射为蛇形命名："pig_table"
  private Long id; // 默认主键为"id"，是自动生成
  private String pigName; // 数据库普通列，默认驼峰映射为蛇形命名："pig_name"
}`

const code2 = `PignooConfig config = new PignooConfig();

// 设置表名、列名与Java中类、属性名一致，不做驼峰映射
config.setNamingMode(NamingMode.SAME);

// 设置主键不由数据库自动生成
config.setAutoPrimaryKey(false);

// 使用函数推算出表的主键，例如pig表对应pig_id，user表对应user_id
config.setPrimaryKeyNamingConvention((tableName, className, c, fields) -> {
  return tableName + "_id";
});`

const code3 = `@Data
public class PigTable { // 表名："PigTable"
  private Long PigTable_id; // 映射规则算出主键为名"PigTable_id"，非自动生成
  private String pigName; // 列名"pigName"
}`

const code4 = `@Data
@Table("pig") // 数据库表名
public class Pig {
  @Column(value="id", primaryKey=PrimaryKey.AUTO) // 数据库主键列，自动生成
  private Long id;
  @Column(value="pig_name") // 数据库普通列
  private String pigName;
}`

const code5 = `@Data
public class Pig {
  private Long id;
  private String name;
  private byte[] picture; // 这是一个数据量较大的字段
}

@Link(Pig.class) // PigView类依然映射pig表
@Data
public class PigView {
  private Long id;
  private String name;
  // 无需查询时，去掉picture字段，可以免去读取大字段，提升性能
  private String nick; // 增加非源数据的字段，不会映射到数据库操作上
}
`

const dataTypeTable = [
  { "javaType": "byte | Byte", "sqlType": "tinyint" },
  { "javaType": "short | Short", "sqlType": "smallint" },
  { "javaType": "int | Integer", "sqlType": "int" },
  { "javaType": "long | Long", "sqlType": "bigint" },
  { "javaType": "float | Float", "sqlType": "float" },
  { "javaType": "double | Double", "sqlType": "double" },
  { "javaType": "boolean | Boolean", "sqlType": "bit(1)" },
  { "javaType": "char | Character", "sqlType": "char(1)" },
  { "javaType": "String", "sqlType": "char | varchar | text | longtext" },

  { "javaType": "Date", "sqlType": "datetime" },
  { "javaType": "Instant", "sqlType": "timestamp" },
  { "javaType": "LocalDate", "sqlType": "date" },
  { "javaType": "LocalTime", "sqlType": "time" },
  { "javaType": "LocalDateTime", "sqlType": "datetime" },
  { "javaType": "ZonedDateTime", "sqlType": "timestamp" },
  { "javaType": "OffsetDateTime", "sqlType": "timestamp" },
  { "javaType": "OffsetTime", "sqlType": "time" },

  { "javaType": "BigDecimal", "sqlType": "decimal" },
  { "javaType": "BigInteger", "sqlType": "decimal" },

  { "javaType": "byte[]", "sqlType": "blob" },
  { "javaType": "Enum", "sqlType": "varchar" },
]

</script>

<style lang="less"></style>